import React, { useState, useEffect } from 'react';
import { Play, Download, Clock, AlertCircle, RotateCw } from 'lucide-react';
import useAuthenticatedMediaUrl from '@/hooks/useAuthenticatedMediaUrl';
import CreativePreviewFallback from '@/components/deal/CreativePreviewFallback';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { videoApiClient } from '@/services/videoApi';
import { directorApiClient } from '@/services/directorApi';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';

const PremierePhase = ({
    scriptData,
    jobStatus,
    onGenerateVideo,
    loading,
    aspectRatio = '16:9',
    businessName = '',
}) => {
    const [showRegenModal, setShowRegenModal] = useState(false);
    const [posterFailed, setPosterFailed] = useState(false);

    const normalizeMediaUrl = (url) => {
        if (typeof url !== 'string' || !url) return '';
        if (
            url.startsWith('http://')
            || url.startsWith('https://')
            || url.startsWith('blob:')
            || url.startsWith('data:')
        ) {
            return url;
        }
        if (url.startsWith('/')) {
            return `${directorApiClient.baseUrl}${url}`;
        }
        return url;
    };

    const totalDuration = scriptData?.scenes?.reduce((sum, scene) => sum + (scene.duration || 0), 0) || 0;
    const sceneCount = scriptData?.scenes?.length || 0;
    const resolvedJobId = jobStatus?.job_id || jobStatus?.jobId;

    const VIDEO_GENERATION_STATES = ['processing', 'running', 'queued', 'pending'];
    const completedAt = jobStatus?.completed_at || jobStatus?.completedAt;
    const normalizedJobState = String(jobStatus?.state || jobStatus?.status || '').toLowerCase();
    const hasCompletedRender = ['completed', 'done', 'ready', 'success', 'succeeded'].includes(normalizedJobState) || Boolean(completedAt);
    const rawVideoUrl = normalizeMediaUrl(
        scriptData?.final_video_url
        || jobStatus?.assets?.video_url
        || jobStatus?.video_url
        || (resolvedJobId && hasCompletedRender ? directorApiClient.getVideoAssetUrl(resolvedJobId) : ''),
    );
    const previewPoster = normalizeMediaUrl(
        jobStatus?.assets?.thumbnail_url
        || jobStatus?.thumbnail_url
        || (resolvedJobId && hasCompletedRender ? videoApiClient.getThumbnailUrl(resolvedJobId) : ''),
    );
    const jobState = jobStatus?.state || (rawVideoUrl ? 'completed' : null);
    const isVideoGenerating = VIDEO_GENERATION_STATES.includes(jobState);
    const progressPercent = jobStatus?.progress?.percent || 0;

    useEffect(() => {
        setPosterFailed(false);
    }, [previewPoster]);
    const {
        resolvedUrl: playablePreviewVideoUrl,
        isLoading: isLoadingPreviewVideo,
    } = useAuthenticatedMediaUrl(rawVideoUrl);
    const downloadHref = rawVideoUrl;
    const hasFinalVideo = Boolean(downloadHref);

    const formatLabel = aspectRatio === '9:16' ? 'Social (9:16)' : aspectRatio === '16:9' ? 'TV (16:9)' : aspectRatio;
    const titleName = businessName ? `${businessName} final cut` : 'Director final cut';

    const statusConfig = (() => {
        switch (jobState) {
            case 'completed':
                return {
                    hero: 'Final cut ready',
                    badgeLabel: 'Ready for review',
                    badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-700',
                    summary: 'Review the finished render, then download the final video or regenerate from the same Director setup.',
                    actionTitle: 'Finalize this cut',
                    actionBody: 'The standalone Director render is complete and ready to export.',
                    primaryLabel: 'Download final video',
                    primaryIcon: Download,
                };
            case 'processing':
            case 'running':
                return {
                    hero: 'Rendering in progress',
                    badgeLabel: 'Rendering',
                    badgeClass: 'border-blue-200 bg-blue-50 text-blue-700',
                    summary: 'Director is assembling the final render from the approved brief and saved studio scenes.',
                    actionTitle: 'Rendering the final cut',
                    actionBody: 'Keep this tab open or come back in a moment. The preview and export action will unlock as soon as the render completes.',
                    primaryLabel: 'Generating...',
                    primaryIcon: Clock,
                };
            case 'queued':
            case 'pending':
                return {
                    hero: 'Queued for render',
                    badgeLabel: 'Queued',
                    badgeClass: 'border-slate-200 bg-slate-100 text-slate-700',
                    summary: 'The final cut is queued and will start rendering shortly.',
                    actionTitle: 'Queued to render',
                    actionBody: 'Director has everything it needs. Rendering will begin automatically as capacity opens up.',
                    primaryLabel: 'Queued',
                    primaryIcon: Clock,
                };
            case 'failed':
                return {
                    hero: 'Render needs attention',
                    badgeLabel: 'Failed',
                    badgeClass: 'border-red-200 bg-red-50 text-red-700',
                    summary: 'The last final render did not complete. Review the error and retry from this Director flow.',
                    actionTitle: 'Retry final render',
                    actionBody: 'The current final output was not produced successfully. Start a new render from the saved project state.',
                    primaryLabel: 'Retry generation',
                    primaryIcon: RotateCw,
                };
            default:
                return {
                    hero: 'Ready to render',
                    badgeLabel: 'Not started',
                    badgeClass: 'border-slate-200 bg-slate-100 text-slate-700',
                    summary: 'The brief and studio work are in place. Generate the final video when you are ready to review the finished cut.',
                    actionTitle: 'Create the final video',
                    actionBody: 'Director will compile the saved scenes into one exportable final cut.',
                    primaryLabel: 'Generate final video',
                    primaryIcon: Play,
                };
        }
    })();

    return (
        <div className="flex min-h-0 flex-1 overflow-y-auto bg-gradient-to-br from-background to-muted/40">
            <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
                <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <Badge variant="outline" className={`rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] ${statusConfig.badgeClass}`}>
                            {statusConfig.hero}
                        </Badge>
                        <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                            Review the final cut
                        </h1>
                        <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                            {statusConfig.summary}
                        </p>
                    </div>
                    <Badge variant="outline" className={`w-fit rounded-full px-3 py-1 text-xs ${statusConfig.badgeClass}`}>
                        Director Studio
                    </Badge>
                </div>

                <div className="grid gap-6 xl:grid-cols-[1.45fr_0.95fr]">
                    <Card className="overflow-hidden border-slate-200 pt-0 shadow-sm">
                        <CardContent className="p-0">
                            <div className="relative aspect-video bg-slate-950">
                                {playablePreviewVideoUrl ? (
                                    <video
                                        key={playablePreviewVideoUrl}
                                        src={playablePreviewVideoUrl}
                                        poster={previewPoster && !posterFailed ? previewPoster : undefined}
                                        controls
                                        playsInline
                                        preload="metadata"
                                        className="h-full w-full object-cover"
                                    />
                                ) : previewPoster && !posterFailed ? (
                                    <img
                                        src={previewPoster}
                                        alt={`${titleName} preview`}
                                        className="h-full w-full object-cover opacity-80"
                                        onError={() => setPosterFailed(true)}
                                    />
                                ) : (
                                    <CreativePreviewFallback
                                        label={isVideoGenerating ? 'Preview rendering' : jobState === 'failed' ? 'Preview unavailable' : 'Final preview not ready'}
                                    />
                                )}

                                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/55 via-slate-950/10 to-transparent" />
                                <Badge className="absolute left-4 top-4 border-0 bg-black/55 text-white">
                                    {totalDuration || 0}s · {formatLabel}
                                </Badge>
                                <Badge className={`absolute right-4 top-4 border ${statusConfig.badgeClass}`}>
                                    {statusConfig.badgeLabel}
                                </Badge>

                                {isLoadingPreviewVideo ? (
                                    <div className="absolute inset-0 flex items-center justify-center bg-slate-950/60 backdrop-blur-[1px]">
                                        <div className="flex items-center gap-2 rounded-full bg-white/92 px-4 py-2 text-sm font-medium text-slate-950 shadow-sm">
                                            <RotateCw className="h-4 w-4 animate-spin" />
                                            Loading video preview
                                        </div>
                                    </div>
                                ) : null}

                                {!playablePreviewVideoUrl ? (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-sm">
                                            <Play className="ml-0.5 h-7 w-7 text-slate-950" />
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="space-y-5 p-6">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div>
                                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Output summary</p>
                                        <h2 className="mt-2 text-2xl font-semibold text-slate-950">{titleName}</h2>
                                        <p className="mt-2 text-sm leading-6 text-slate-600">
                                            {jobState === 'completed'
                                                ? 'The final Director render is ready for review and export.'
                                                : statusConfig.actionBody}
                                        </p>
                                    </div>
                                    <div className={`rounded-2xl border px-3 py-2 text-sm font-medium ${statusConfig.badgeClass}`}>
                                        {statusConfig.badgeLabel}
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                                        {formatLabel}
                                    </Badge>
                                    <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                                        {totalDuration || 0}s total
                                    </Badge>
                                    <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700">
                                        {sceneCount} scene{sceneCount === 1 ? '' : 's'}
                                    </Badge>
                                </div>

                                <Separator />

                                {jobState === 'failed' ? (
                                    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                                        {jobStatus?.error || 'The final video could not be generated. Retry the render to create a fresh output.'}
                                    </div>
                                ) : null}

                                {jobStatus?.message && isVideoGenerating ? (
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                        {jobStatus.message}
                                    </div>
                                ) : null}

                                {scriptData?.concept ? (
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Creative brief</p>
                                        <p className="mt-3 text-sm leading-6 text-slate-600">
                                            {typeof scriptData.concept === 'string' ? scriptData.concept : ''}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        </CardContent>
                    </Card>

                    <div className="space-y-6">
                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-5">
                                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Next action</p>
                                <h2 className="mt-3 text-2xl font-semibold leading-tight text-slate-950">{statusConfig.actionTitle}</h2>
                                <p className="mt-2 text-sm leading-6 text-slate-600">
                                    {statusConfig.actionBody}
                                </p>

                                {isVideoGenerating ? (
                                    <div className="mt-5 space-y-3">
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="flex items-center justify-between gap-4">
                                                <span className="text-sm font-medium text-slate-950">Render progress</span>
                                                <span className="text-sm text-slate-500">{progressPercent}%</span>
                                            </div>
                                            <Progress value={progressPercent} className="mt-3 h-2" />
                                        </div>
                                    </div>
                                ) : null}

                                <div className="mt-5 space-y-3">
                                    {hasFinalVideo ? (
                                        <Button size="lg" className="h-11 w-full rounded-xl bg-coral text-white hover:bg-coral/90" asChild>
                                            <a href={downloadHref} target="_blank" rel="noopener noreferrer" download>
                                                <Download className="h-4 w-4" />
                                                Download final video
                                            </a>
                                        </Button>
                                    ) : null}

                                    {!hasFinalVideo ? (
                                        <Button
                                            size="lg"
                                            onClick={onGenerateVideo}
                                            disabled={loading || isVideoGenerating}
                                            className="h-11 w-full rounded-xl bg-coral text-white hover:bg-coral/90"
                                        >
                                            <statusConfig.primaryIcon className={`h-4 w-4${isVideoGenerating ? ' animate-spin' : ''}`} />
                                            {statusConfig.primaryLabel}
                                        </Button>
                                    ) : null}

                                    {hasFinalVideo ? (
                                        <Button
                                            variant="outline"
                                            onClick={() => setShowRegenModal(true)}
                                            className="h-11 w-full rounded-xl bg-white"
                                        >
                                            <RotateCw className="h-4 w-4" />
                                            Create another render
                                        </Button>
                                    ) : null}
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-slate-200 shadow-sm">
                            <CardContent className="p-5">
                                <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">Render details</p>
                                <div className="mt-4 space-y-3">
                                    <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <div>
                                            <p className="text-sm text-slate-500">Status</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-950">{statusConfig.badgeLabel}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-slate-500">Scenes</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-950">{sceneCount}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <div>
                                            <p className="text-sm text-slate-500">Format</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-950">{formatLabel}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-slate-500">Duration</p>
                                            <p className="mt-1 text-sm font-semibold text-slate-950">{totalDuration || 0}s</p>
                                        </div>
                                    </div>

                                    {jobStatus?.started_at ? (
                                        <div className="rounded-2xl border border-slate-200 p-4">
                                            <p className="text-sm text-slate-500">Render activity</p>
                                            <div className="mt-2 flex items-start gap-3">
                                                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
                                                <div className="space-y-1 text-sm leading-6 text-slate-600">
                                                    <p>Started at {new Date(jobStatus.started_at).toLocaleTimeString()}</p>
                                                    {jobStatus?.message ? <p>{jobStatus.message}</p> : null}
                                                </div>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>

            <Dialog open={showRegenModal} onOpenChange={setShowRegenModal}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle>Regenerate Final Video?</DialogTitle>
                        <DialogDescription>
                            The current final video will be replaced with a newly generated one. This process may take several minutes and cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRegenModal(false)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                setShowRegenModal(false);
                                onGenerateVideo();
                            }}
                        >
                            Regenerate
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PremierePhase;
